import { X, ShoppingBag, Trash2, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TagVisual from './TagVisual';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { load as loadCashfree } from '@cashfreepayments/cashfree-js';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getCart() { try { return JSON.parse(localStorage.getItem('cart') || '[]'); } catch { return []; } }
function setCart(items) { localStorage.setItem('cart', JSON.stringify(items)); window.dispatchEvent(new Event('cart:update')); }

export function addToCart(product) {
  const cart = getCart();
  const existing = cart.find((i) => i.id === product.id);
  if (existing) existing.qty += 1;
  else cart.push({ id: product.id, name: product.name, price: product.price, qty: 1 });
  setCart(cart);
}

export default function CartSheet({ open, onClose }) {
  const [items, setItems] = useState(getCart());
  const [placing, setPlacing] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [ship, setShip] = useState({ address: '', city: '', district: '', state: '', pincode: '' });
  const [paymentMode, setPaymentMode] = useState('online');
  const [source, setSource] = useState('');
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const refresh = () => setItems(getCart());
    window.addEventListener('cart:update', refresh);
    return () => window.removeEventListener('cart:update', refresh);
  }, []);

  useEffect(() => {
    if (open) { setItems(getCart()); document.body.style.overflow = 'hidden'; }
    else document.body.style.overflow = '';
  }, [open]);

  useEffect(() => {
    if (open && user && user.role === 'customer' && token) {
      axios.get(`${API}/wallet`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setWalletBalance(r.data.balance || 0))
        .catch(() => {});
      // Pre-fill shipping from user profile
      axios.get(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setShip({
          address: r.data.address || '',
          city: r.data.city || '',
          district: r.data.district || '',
          state: r.data.state || '',
          pincode: r.data.pincode || '',
        }))
        .catch(() => {});
    }
  }, [open, user, token]);

  if (!open) return null;

  const remove = (id) => setCart(items.filter((i) => i.id !== id));
  const inc = (id) => setCart(items.map((i) => (i.id === id ? { ...i, qty: i.qty + 1 } : i)));
  const dec = (id) => setCart(items.flatMap((i) => (i.id === id ? (i.qty > 1 ? [{ ...i, qty: i.qty - 1 }] : []) : [i])));
  const total = items.reduce((s, i) => s + i.price * i.qty, 0);
  const walletApplied = useWallet ? Math.min(walletBalance, total) : 0;
  const payable = Math.max(0, total - walletApplied);

  const checkout = async () => {
    if (!user) {
      toast({ title: 'Please login to checkout' });
      onClose();
      navigate('/login?redirect=/shop');
      return;
    }
    setPlacing(true);
    try {
      // Basic shipping validation — need at least city + state + pincode to keep sales analytics clean
      if (!ship.city.trim() || !ship.state.trim() || !ship.pincode.trim() || !ship.address.trim()) {
        toast({ title: 'Please add shipping details', description: 'Address, city, state and PIN are required.' });
        setPlacing(false);
        return;
      }
      const referral_code = localStorage.getItem('referral_code') || undefined;
      const { data: order } = await axios.post(`${API}/orders`, {
        items: items.map((i) => ({ product_id: i.id, name: i.name, price: i.price, qty: i.qty })),
        total,
        referral_code,
        use_wallet_points: walletApplied,
        ship_address: ship.address,
        ship_city: ship.city,
        ship_district: ship.district,
        ship_state: ship.state,
        ship_pincode: ship.pincode,
        payment_mode: paymentMode,
        source: source || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });

      // If fully paid via wallet, backend already marked paid
      if (order.payable <= 0) {
        setCart([]);
        localStorage.removeItem('referral_code');
        toast({ title: 'Order placed 🎉', description: `Order #${order.order_number} · paid via wallet` });
        onClose();
        navigate('/dashboard');
        return;
      }

      // COD: skip the online payment gateway — order stays confirmed, amount due on delivery
      if (paymentMode === 'cod') {
        setCart([]);
        localStorage.removeItem('referral_code');
        toast({ title: 'COD order placed 📦', description: `Order #${order.order_number} · ₹${order.payable} due on delivery` });
        onClose();
        navigate('/dashboard');
        return;
      }

      const { data: session } = await axios.post(`${API}/payments/init`, { order_id: order.id }, { headers: { Authorization: `Bearer ${token}` } });

      if (session.demo) {
        toast({ title: 'Payment demo mode', description: 'Cashfree not configured — simulating success.' });
        await new Promise((r) => setTimeout(r, 1200));
        try { await axios.post(`${API}/payments/demo/complete/${order.order_number}`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch (_) {}
        setCart([]);
        localStorage.removeItem('referral_code');
        toast({ title: 'Payment successful 🎉', description: `Order #${order.order_number} · ₹${order.payable}` });
        onClose();
        navigate('/dashboard');
      } else {
        const cashfree = await loadCashfree({ mode: 'sandbox' });
        const result = await cashfree.checkout({ paymentSessionId: session.payment_session_id, redirectTarget: '_modal' });
        if (result?.error) {
          toast({ title: 'Payment cancelled', description: result.error.message || 'Please try again.' });
        } else {
          const { data: statusData } = await axios.get(`${API}/payments/status/${order.order_number}`, { headers: { Authorization: `Bearer ${token}` } });
          if (['PAID', 'SUCCESS'].includes((statusData.order_status || '').toUpperCase())) {
            setCart([]);
            localStorage.removeItem('referral_code');
            toast({ title: 'Payment successful 🎉', description: `Order #${order.order_number} · ₹${order.payable}` });
            onClose();
            navigate('/dashboard');
          }
        }
      }
    } catch (e) {
      toast({ title: 'Checkout failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setPlacing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-white h-full flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-5 border-b">
          <div className="flex items-center gap-2"><ShoppingBag size={18} /><h3 className="font-semibold">Your Cart</h3></div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-black/5"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-16 text-neutral-500">
              <ShoppingBag size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">Your cart is empty</p>
              <Link to="/shop" onClick={onClose} className="btn-primary inline-flex mt-5 text-sm">Browse tags</Link>
            </div>
          )}
          {items.map((i) => (
            <div key={i.id} className="flex gap-3 items-center card-soft p-3">
              <div className="w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center bg-cream">
                <TagVisual size={60} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold">{i.name}</p>
                <p className="text-xs text-neutral-500 mt-0.5">₹{i.price}</p>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => dec(i.id)} className="w-6 h-6 rounded-full border text-sm">-</button>
                  <span className="text-sm w-5 text-center">{i.qty}</span>
                  <button onClick={() => inc(i.id)} className="w-6 h-6 rounded-full border text-sm">+</button>
                </div>
              </div>
              <button onClick={() => remove(i.id)} className="p-2 text-neutral-500 hover:text-red-500"><Trash2 size={16} /></button>
            </div>
          ))}

          {user && items.length > 0 && walletBalance > 0 && (
            <button onClick={() => setUseWallet(!useWallet)} className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 transition-colors ${useWallet ? 'border-navy bg-navy/5' : 'border-black/10'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${useWallet ? 'bg-navy text-white' : 'bg-navy/10 text-navy'}`}><Wallet size={16} /></div>
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold">Use wallet points</p>
                <p className="text-xs text-neutral-500">Balance ₹{walletBalance} · Apply up to ₹{Math.min(walletBalance, total)}</p>
              </div>
              <div className={`w-4 h-4 rounded border-2 ${useWallet ? 'border-navy bg-navy' : 'border-black/20'}`}></div>
            </button>
          )}

          {user && items.length > 0 && (
            <div className="card-soft p-4 space-y-2" data-testid="cart-shipping-form">
              <p className="text-sm font-semibold flex items-center gap-2"><ShoppingBag size={14} /> Shipping details</p>
              <textarea
                rows={2}
                value={ship.address}
                onChange={(e) => setShip({ ...ship, address: e.target.value })}
                placeholder="Flat / house no, street, area"
                className="w-full text-sm px-3 py-2 rounded-lg border border-black/10 resize-none focus:outline-none focus:ring-2 focus:ring-navy/30"
                data-testid="cart-ship-address"
              />
              <div className="grid grid-cols-2 gap-2">
                <input value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} placeholder="City*" className="text-sm px-3 py-2 rounded-lg border border-black/10 focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid="cart-ship-city" />
                <input value={ship.district} onChange={(e) => setShip({ ...ship, district: e.target.value })} placeholder="District" className="text-sm px-3 py-2 rounded-lg border border-black/10 focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid="cart-ship-district" />
                <input value={ship.state} onChange={(e) => setShip({ ...ship, state: e.target.value })} placeholder="State*" className="text-sm px-3 py-2 rounded-lg border border-black/10 focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid="cart-ship-state" />
                <input value={ship.pincode} onChange={(e) => setShip({ ...ship, pincode: e.target.value })} placeholder="PIN code*" maxLength={6} className="text-sm px-3 py-2 rounded-lg border border-black/10 focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid="cart-ship-pincode" />
              </div>
              <p className="text-[11px] text-neutral-500">Saved to your profile so future orders auto-fill.</p>

              <div className="pt-3 border-t border-black/5 grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-neutral-500 uppercase tracking-wider">Payment mode</label>
                  <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} className="mt-1 w-full text-sm px-3 py-2 rounded-lg border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid="cart-payment-mode">
                    <option value="online">Online (UPI / Card)</option>
                    <option value="cod">Cash on Delivery</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-neutral-500 uppercase tracking-wider">Where did you hear about us?</label>
                  <select value={source} onChange={(e) => setSource(e.target.value)} className="mt-1 w-full text-sm px-3 py-2 rounded-lg border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-navy/30" data-testid="cart-source">
                    <option value="">Prefer not to say</option>
                    <option value="online">Online</option>
                    <option value="social_media">Social media</option>
                    <option value="friends_family">Friends / Family</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t p-5 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-neutral-600">Subtotal</span><span>₹{total}</span></div>
            {walletApplied > 0 && (
              <div className="flex justify-between text-sm text-navy"><span>Wallet points used</span><span>-₹{walletApplied}</span></div>
            )}
            <div className="flex justify-between text-base font-semibold"><span>Payable</span><span>₹{payable}</span></div>
            <p className="text-xs text-neutral-500 pt-1">Free shipping · Powered by Cashfree · COD available</p>
            <button disabled={placing} onClick={checkout} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="cart-checkout-btn">
              {placing ? 'Processing…' : payable === 0 ? `Pay with wallet · ₹${walletApplied}` : paymentMode === 'cod' ? `Place COD order · ₹${payable}` : `Pay ₹${payable}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
